import { Controller } from '@hotwired/stimulus'

// Список выбранных для сравнения slug'ов переживает переход между страницами
// товаров (в отличие от состояния конструктора — тот живёт только в URL),
// поэтому и хранится не там, а в localStorage: пользователь ходит по разным
// карточкам, жмёт "Add to compare" на каждой, и только потом одним переходом
// попадает на страницу результата со всеми накопленными.
const STORAGE_KEY = 'padelmesh:compare-slugs'

// Совпадает с ComparesController::INDEXABLE_ITEMS.max (и с одноимённой
// константой в compare_picker_controller.js): 2-3 товара получают путь в
// адресе (свой индексируемый лендинг), 4-10 — ?products= с noindex.
const PATH_ITEMS_MAX = 3

/**
 * @file Кнопки сравнения в hero товара: "Add to compare" переключает текущий
 * товар в накопленном списке (см. STORAGE_KEY), "Compare selected" ведёт на
 * страницу сравнения — при 2+ товарах сразу на результат (см.
 * ComparesController#show), характеристики видны с первого перехода; при
 * ровно одном — в конструктор с этим товаром (см. ComparesController#new,
 * тот же ?add=, что и хлебная крошка результата) — сравнивать не с чем, но
 * дать посетителю сразу добрать вторую ракетку в уже открытом конструкторе
 * удобнее, чем гасить кнопку. Пока список пуст, сравнивать не с чем вовсе —
 * ссылка пригашена (тот же приём, что у submit конструктора, см.
 * compare_picker_controller.js#updateSubmit). Бейдж над ней — число товаров
 * в списке.
 */
export default class CompareBarController extends Controller {
  static targets = ['addButton', 'compareLink', 'badge']

  static values = {
    slug: String,
    maxItems: { type: Number, default: 10 },
    comparePath: String,
    compareQueryPath: String,
    addLabel: String,
    removeLabel: String
  }

  // Другая вкладка того же браузера могла изменить список — держим бейдж и
  // подписи кнопок в согласии с ним, а не только с действиями в этой вкладке.
  #handleStorage = (event) => {
    if (event.key === STORAGE_KEY) this.#render()
  }

  connect() {
    this.#render()
    window.addEventListener('storage', this.#handleStorage)
  }

  disconnect() {
    window.removeEventListener('storage', this.#handleStorage)
  }

  toggle() {
    const list = this.#readList()
    const index = list.indexOf(this.slugValue)

    if (index === -1) {
      if (list.length >= this.maxItemsValue) return
      list.push(this.slugValue)
    } else {
      list.splice(index, 1)
    }

    this.#writeList(list)
    this.#render()
  }

  // pointer-events-none гасит только мышь: до пригашенной ссылки всё ещё
  // можно дойти табом и нажать Enter — не пускаем на сравнение, которого нет.
  submit(event) {
    if (this.#readList().length === 0) event.preventDefault()
  }

  #readList() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      const parsed = raw ? JSON.parse(raw) : []
      return Array.isArray(parsed)
        ? parsed.filter((slug) => typeof slug === 'string')
        : []
    } catch {
      return []
    }
  }

  #writeList(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
    } catch {
      // Приватный режим браузера и подобное — список просто не переживёт
      // переход на другую страницу, ничего не ломаем попыткой записи.
    }
  }

  #render() {
    const list = this.#readList()
    const included = list.includes(this.slugValue)

    if (this.hasAddButtonTarget) {
      // Список полон, а текущего товара в нём нет — добавлять больше некуда:
      // у disabled-кнопки штатный пригашенный вид (см. ui/_button.scss).
      this.addButtonTarget.disabled =
        list.length >= this.maxItemsValue && !included
      this.addButtonTarget.setAttribute('aria-pressed', String(included))

      // .btn__text/<use> — внутренняя разметка _button.slim, отдельных
      // Stimulus-таргетов под них компонент не даёт: SSR рисует "plus" и
      // addLabel по умолчанию (частый случай первого захода), а дальше
      // просто подменяем текст и href у <use>, без пересборки кнопки на
      // сервере.
      const label = this.addButtonTarget.querySelector('.btn__text')
      if (label) {
        label.textContent = included
          ? this.removeLabelValue
          : this.addLabelValue
      }

      const iconUse = this.addButtonTarget.querySelector('use')
      if (iconUse) {
        iconUse.setAttribute('href', included ? '#icon-check' : '#icon-plus')
      }
    }

    if (this.hasCompareLinkTarget) {
      const ready = list.length >= 1

      // href при неготовности — "#", а не SSR-адрес конструктора: клик всё
      // равно погашен (pointer-events-none + preventDefault в #submit), а
      // осмысленный адрес на пригашенной кнопке только путал бы при
      // копировании ссылки через контекстное меню.
      this.compareLinkTarget.href = ready ? this.#compareUrl(list) : '#'
      this.compareLinkTarget.classList.toggle('opacity-50', !ready)
      this.compareLinkTarget.classList.toggle('pointer-events-none', !ready)
      this.compareLinkTarget.setAttribute('aria-disabled', String(!ready))
    }

    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = String(list.length)
      // style.display, а не hidden-атрибут: у бейджа в _hero.scss свой
      // display: flex неслоёным правилом — он бы перебивал [hidden] из
      // UA-стиля (тот же приём, что и у стрелок сравнения, см.
      // compare_scroll_sync_controller.js).
      this.badgeTarget.style.display = list.length === 0 ? 'none' : ''
    }
  }

  // Один товар — сравнивать не с чем, поэтому не страница результата
  // (ComparesController#show ей и не считает: там redirect_to new_compare_path
  // при slugs.size < 2), а конструктор с этим товаром уже в слоте — тот же
  // ?add=, что и у хлебной крошки на странице результата (см.
  // compares/show.slim). 2-3 — путь (см. PATH_ITEMS_MAX), 4-10 — ?products=:
  // тот же выбор формы адреса, что у compare_picker_controller.js#compareUrl.
  // Несортированный порядок не страшен — сервер сам приводит его 301-м к
  // каноническому (см. ComparesController#canonical_redirect_for).
  #compareUrl(slugs) {
    if (slugs.length === 1) {
      const url = new URL(this.compareQueryPathValue, window.location.origin)
      url.searchParams.set('add', slugs[0])
      return `${url.pathname}${url.search}`
    }

    if (slugs.length <= PATH_ITEMS_MAX) {
      return this.comparePathValue.replace('__SLUGS__', slugs.join('/'))
    }

    const url = new URL(this.compareQueryPathValue, window.location.origin)
    url.searchParams.set('products', slugs.join(','))
    return `${url.pathname}${url.search}`
  }
}
